import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Popover } from "./popover";
import { CustomRangeCalendar } from "./custom-range-calendar";

// Shared "Daily / Weekly / Monthly" period filter, extended with "All time" and
// "Custom Range" (a real calendar range-picker) — used everywhere in the app that
// previously had just the plain Daily/Weekly/Monthly dropdown.
export type PeriodValue = "all_time" | "daily" | "weekly" | "monthly" | "custom";

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export const EMPTY_DATE_RANGE: DateRange = { start: null, end: null };

export const PERIOD_FILTER_OPTIONS: { value: PeriodValue; label: string }[] = [
  { value: "all_time", label: "All time" },
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
  { value: "daily", label: "Daily" },
  { value: "custom", label: "Custom Range" },
];

const formatShort = (d: Date) => d.toLocaleDateString("en-US", { day: "numeric", month: "short" });

export const getPeriodLabel = (period: PeriodValue): string =>
  PERIOD_FILTER_OPTIONS.find((o) => o.value === period)?.label || "All time";

// The dropdown's date-range subtext (e.g. "1 Aug - 19 Aug") — "so far" ranges for
// daily/weekly/monthly (start of period through today), the picked span for custom, and
// nothing for all time.
export const getPeriodDateRangeLabel = (period: PeriodValue, customRange: DateRange): string => {
  const today = new Date();
  if (period === "daily") return formatShort(today);

  if (period === "weekly") {
    const start = new Date(today);
    const dayIndex = start.getDay();
    const diffToMonday = dayIndex === 0 ? 6 : dayIndex - 1;
    start.setDate(start.getDate() - diffToMonday);
    return `${formatShort(start)} - ${formatShort(today)}`;
  }

  if (period === "monthly") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return `${formatShort(start)} - ${formatShort(today)}`;
  }

  if (period === "custom" && customRange.start && customRange.end) {
    return `${formatShort(customRange.start)} - ${formatShort(customRange.end)}`;
  }

  return "";
};

// Builds the query params a typical `period`-based endpoint expects. Pages can spread this
// into their own request params (adding extra params like `period_days` themselves).
// ASSUMPTION FLAGGED: `period: 'all_time'` and the `start_date`/`end_date` (YYYY-MM-DD)
// custom-range params follow the existing daily/weekly/monthly naming already used across
// these endpoints, but aren't individually confirmed on every endpoint this is used with.
// `omitAllTime` sends no period param at all for "All time", for endpoints that treat a
// missing period as all-time.
export const buildPeriodRequestParams = (
  period: PeriodValue,
  customRange: DateRange,
  options: { omitAllTime?: boolean } = {}
): Record<string, any> | null => {
  if (period === "custom") {
    if (!customRange.start || !customRange.end) return null;
    return {
      period: "custom",
      start_date: customRange.start.toISOString().slice(0, 10),
      end_date: customRange.end.toISOString().slice(0, 10),
    };
  }
  if (period === "all_time" && options.omitAllTime) return {};
  return { period };
};

interface PeriodDropdownProps {
  value: PeriodValue;
  onChange: (period: PeriodValue) => void;
  customRange: DateRange;
  onCustomRangeChange: (range: DateRange) => void;
  triggerClassName?: string;
  menuWidthClassName?: string;
  showDateRangeLabel?: boolean;
}

export const PeriodDropdown: React.FC<PeriodDropdownProps> = ({
  value,
  onChange,
  customRange,
  onCustomRangeChange,
  triggerClassName,
  menuWidthClassName = "w-40",
  showDateRangeLabel = true,
}) => {
  const [pickerStep, setPickerStep] = useState<"list" | "calendar">("list");
  const currentLabel = getPeriodLabel(value);
  const dateRangeLabel = showDateRangeLabel ? getPeriodDateRangeLabel(value, customRange) : "";

  return (
    <Popover
      trigger={({ toggle, open }) => (
        <button
          onClick={toggle}
          className={
            triggerClassName ||
            "px-4 py-2 rounded-full bg-white border border-[#DBDEF0] hover:bg-gray-50 text-[#003320] font-medium transition-colors text-sm flex items-center gap-2"
          }
        >
          {currentLabel}
          {dateRangeLabel && <span className="text-xs text-gray-400 font-normal">{dateRangeLabel}</span>}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      menuClassName={
        pickerStep === "calendar"
          ? "bg-white rounded-lg shadow-lg border border-[#DBDEF0] overflow-hidden"
          : `${menuWidthClassName} bg-white rounded-lg shadow-lg border border-[#DBDEF0] overflow-hidden`
      }
    >
      {(close) =>
        pickerStep === "calendar" ? (
          <CustomRangeCalendar
            initialStart={customRange.start}
            initialEnd={customRange.end}
            onApply={(start, end) => {
              onCustomRangeChange({ start, end });
              onChange("custom");
              setPickerStep("list");
              close();
            }}
            onCancel={() => setPickerStep("list")}
          />
        ) : (
          PERIOD_FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                if (option.value === "custom") {
                  setPickerStep("calendar");
                  return;
                }
                onChange(option.value);
                close();
              }}
              className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                value === option.value ? "bg-[#E5F6F0] text-[#003320] font-medium" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {option.label}
            </button>
          ))
        )
      }
    </Popover>
  );
};
